import React, { useState } from "react";
import Textfield from "../textfield";
import Timepicker from "../timepicker";

function DateRangeTimePicker({
  className,
  onChange,
  startDate,
  endDate,
  startTime,
  endTime,
  dType = "date",
  startDateName = "signupDateFrom",
  endDateName = "signupDateInto",
  startTimeName = "startTimeName",
  endTimeName = "endTimeName",
  min = new Date().toISOString().split("T")[0],
  minTime = "00:00",
  maxTime = "24:00",
  minEndDate = startDate,
  invalid = false,
  disabled = false,
  // New prop to specify output format
  outputFormat = "string", // "string" | "date" | "auto"
}) {
  // Utility function to convert Date to YYYY-MM-DD string
  const dateToString = (date) => {
    if (!date) return "";
    if (typeof date === "string") return date;
    if (date instanceof Date) {
      return date.toISOString().split("T")[0];
    }
    return "";
  };

  // Utility function to convert string to Date object
  const stringToDate = (dateString) => {
    if (!dateString) return null;
    if (dateString instanceof Date) return dateString;
    if (typeof dateString === "string") {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  };

  // Utility function to format output based on outputFormat setting
  const formatOutput = (value, fieldType) => {
    if (fieldType === "time") return value; // Time always stays as string

    switch (outputFormat) {
      case "date":
        return stringToDate(value);
      case "string":
        return typeof value === "string" ? value : dateToString(value);
      case "auto":
        // Auto-detect based on input type
        if (typeof startDate === "string" || typeof endDate === "string") {
          return typeof value === "string" ? value : dateToString(value);
        } else {
          return stringToDate(value);
        }
      default:
        return value;
    }
  };

  // Enhanced onChange handler
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Determine if this is a date or time field
    const isDateField = name === startDateName || name === endDateName;
    const isTimeField = name === startTimeName || name === endTimeName;

    // Format the value based on field type and outputFormat
    const formattedValue = isDateField ? formatOutput(value, "date") : value;

    // Create a new event object with formatted value if needed
    const formattedEvent = {
      ...e,
      target: {
        ...e.target,
        value: formattedValue,
      },
    };

    // Call the parent onChange with the formatted event and field name
    if (onChange) {
      onChange(formattedEvent, name);
    }
  };

  // Convert values for display in input fields (always strings for HTML inputs)
  const displayStartDate = dateToString(startDate);
  const displayEndDate = dateToString(endDate);

  return (
    <div className={`grid grid-row-2 gap-2 ${className}`}>
      <div className="flex gap-2">
        <p className="col-span-1 my-auto">Start: </p>
        <Textfield
          type={dType}
          disabled={disabled}
          value={displayStartDate}
          onChange={handleChange}
          classNameParent="col-span-2 w-[75%]"
          name={startDateName}
          min={min}
          maxStartDate={min}
          invalid={invalid}
        />
        <Timepicker
          value={startTime}
          disabled={disabled}
          classNameParent="col-span-2 w-[35%]"
          onChange={handleChange}
          min={minTime}
          max={maxTime}
          name={startTimeName}
        />
      </div>
      <div className="flex gap-2">
        <p className="col-span-1 my-auto mr-[5px]">End: </p>
        <Textfield
          type={dType}
          disabled={disabled}
          value={displayEndDate}
          onChange={handleChange}
          classNameParent={`col-span-2 w-[75%]`}
          name={endDateName}
          min={min}
          invalid={invalid}
        />
        <Timepicker
          value={endTime}
          disabled={disabled}
          classNameParent="col-span-2 w-[35%]"
          onChange={handleChange}
          min={minTime}
          max={maxTime}
          name={endTimeName}
        />
      </div>
    </div>
  );
}

export default DateRangeTimePicker;
